import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, ListItemText, Tooltip, IconButton,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { RateCardList, RateCardVehicle, ZoneCardSummary, getRateCards } from '../services/rateCardService';
import RateCardEditor from './rateCards/RateCardEditor';
import ZonePicker, { cityOf } from './rateCards/ZonePicker';
import { getApiErrorMessage } from '../lib/toast';
import { useCan } from '../auth/Can';
import HelpDialog from '../components/common/HelpDialog';
import { LOCK_WARNING, PRICING_FLOWS, RATE_CARD_PAGE_SECTIONS } from './rateCards/rateCardHelp';

const ALL = '__all__';

const RateCards: React.FC = () => {
  const { can } = useCan();
  const canWrite = can('pricing:write');
  const [params, setParams] = useSearchParams();
  const list = useQuery({ queryKey: ['rate-cards'], queryFn: getRateCards });
  const data: RateCardList | undefined = list.data;
  const [cityFilter, setCityFilter] = useState(ALL);
  const [addZoneOpen, setAddZoneOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const vehicles: RateCardVehicle[] = data?.vehicles ?? [];
  const vehicleId = Number(params.get('vehicle')) || vehicles[0]?.rideOption.id;
  const vehicle = vehicles.find((v) => v.rideOption.id === vehicleId) ?? vehicles[0];
  const zoneParam = params.get('zone');
  const zoneId = zoneParam ? Number(zoneParam) : null;
  const select = (vId: number, zId: number | null) => setParams(zId === null ? { vehicle: String(vId) } : { vehicle: String(vId), zone: String(zId) });

  const zones: RateCardList['zones'] = data?.zones ?? [];
  const zoneById = useMemo(() => new Map(zones.map((z) => [z.id, z])), [zones]);
  const cities = useMemo(() => Array.from(new Set(zones.map((z) => cityOf(z.city)))).sort(), [zones]);
  const overrides: ZoneCardSummary[] = (vehicle?.zones ?? []).filter((z) => z.isActive);
  const shownOverrides = overrides.filter((z) => cityFilter === ALL || cityOf(z.city) === cityFilter);

  if (list.isLoading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (list.isError || !data) return <Alert severity="error">{getApiErrorMessage(list.error)}</Alert>;

  const zone = zoneId !== null ? zoneById.get(zoneId) : null;
  const scopeLabel = zoneId === null ? 'Global (every zone without an override)' : `${zone?.name ?? `Zone ${zoneId}`} · ${cityOf(zone?.city)}`;

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <ReceiptLongIcon color="secondary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Rate Cards</Typography>
        <Tooltip title="How rate cards work"><IconButton size="small" onClick={() => setHelpOpen(true)}><HelpOutlineIcon fontSize="small" /></IconButton></Tooltip>
        <Box flex={1} />
        <Chip color={data.enabled ? 'success' : 'warning'}
              label={data.enabled ? 'Rate cards ON' : 'Rate cards OFF — legacy pricing (RATE_CARD_PRICING_ENABLED = 0)'} />
      </Stack>
      {!data.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The kill switch is off: every new quote and booking uses the old ride-option prices; rides already booked on a card finish at their booked price. Edits are saved but take effect only when App Config RATE_CARD_PRICING_ENABLED is set back to 1.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Paper variant="outlined" sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}>
          <List dense disablePadding>
            {vehicles.map((v) => {
              const n = v.zones.filter((z) => z.isActive).length;
              return (
                <ListItemButton key={v.rideOption.id} selected={v.rideOption.id === vehicle?.rideOption.id} onClick={() => select(v.rideOption.id, null)}>
                  <ListItemText primary={v.rideOption.label || v.rideOption.type}
                                secondary={`${v.rideOption.type}${v.rideOption.available ? '' : ' · hidden'}${n ? ` · ${n} zone override${n > 1 ? 's' : ''}` : ''}`} />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {vehicle && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} useFlexGap flexWrap="wrap">
                <Chip label="Global" color={zoneId === null ? 'primary' : 'default'} onClick={() => select(vehicle.rideOption.id, null)} />
                <TextField select size="small" label="City" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value={ALL}>All cities</MenuItem>
                  {cities.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                {shownOverrides.map((z) => (
                  <Chip key={z.id} label={`${z.zoneName}${cityFilter === ALL ? ` · ${cityOf(z.city)}` : ''}`}
                        color={zoneId === z.zoneId ? 'primary' : 'default'} variant={z.isSeed ? 'outlined' : 'filled'}
                        onClick={() => select(vehicle.rideOption.id, z.zoneId)} />
                ))}
                {shownOverrides.length === 0 && <Typography variant="body2" color="text.secondary">No zone overrides{cityFilter === ALL ? '' : ` in ${cityFilter}`}.</Typography>}
                <Box flex={1} />
                {canWrite && (
                  <Button size="small" startIcon={<AddLocationAltIcon />} onClick={() => setAddZoneOpen(true)}>Add zone override</Button>
                )}
              </Stack>
            </Paper>
          )}
          {vehicle && (
            <RateCardEditor key={`${vehicle.rideOption.id}:${zoneId}`} vehicle={vehicle} zoneId={zoneId} scopeLabel={scopeLabel}
                            list={data} canWrite={canWrite} onRemoved={() => select(vehicle.rideOption.id, null)} />
          )}
        </Box>
      </Stack>

      <Dialog open={addZoneOpen} onClose={() => setAddZoneOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add zone override · {vehicle?.rideOption.label}</DialogTitle>
        <DialogContent>
          <ZonePicker zones={zones} excludeIds={overrides.map((z) => z.zoneId)}
                      onPick={(id) => { if (vehicle) select(vehicle.rideOption.id, id); setAddZoneOpen(false); }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setAddZoneOpen(false)}>Cancel</Button></DialogActions>
      </Dialog>

      <HelpDialog
        open={helpOpen} onClose={() => setHelpOpen(false)} title="How rate cards work"
        intro="Every ride is priced by a rate card: one global card per vehicle type, plus optional zone cards that override it for pickups inside that zone (edit them here or from the zone's page). The card in force at booking is frozen into the ride, so the quote, the booking and the final bill all use the same prices."
        warning={LOCK_WARNING}
        flows={PRICING_FLOWS}
        sections={RATE_CARD_PAGE_SECTIONS}
      />
    </Box>
  );
};

export default RateCards;
